import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Check, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';
import FundEliteBrandMark from '../../components/branding/FundEliteBrandMark';
import {
  getSignupErrorMessage,
  getPostSignupRedirectPath,
  applyPostAuthRedirect,
  AUTH_TOKEN_KEY,
} from '../../utils/authUtils';
import { saveSignupProfileToOnboarding } from '../../utils/onboardingSignupProfile';
import { isFundGeniusSignupHost } from '../../utils/fundgeniusLoginBranding';

const Signup = () => {
  const navigate = useNavigate();
  const { signup, user, loading: authLoading } = useAuth();
  const { resolvedMode } = useTheme();
  const brandTheme = resolvedMode === 'light' ? 'light' : 'dark';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isFundGeniusHost = isFundGeniusSignupHost(hostname);

  useEffect(() => {
    if (!authLoading && user) {
      if (isFundGeniusHost) {
        applyPostAuthRedirect(user, navigate, getPostSignupRedirectPath({ user, hostname }));
        return;
      }
      navigate('/onboarding?step=goals_selection', { replace: true });
    }
  }, [authLoading, user, navigate, isFundGeniusHost, hostname]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Google login can be disabled via environment variable
  // Set to false to temporarily disable Google login
  const isGoogleLoginEnabled = false; // TEMPORARILY DISABLED - was: process.env.REACT_APP_GOOGLE_LOGIN_ENABLED !== 'false';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (formError) setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!formData.name || !formData.email || !formData.password || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(formData.email, formData.password, formData.name);
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await saveSignupProfileToOnboarding(token, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      toast.success('Account created successfully!');
      if (isFundGeniusHost) {
        navigate(getPostSignupRedirectPath({ hostname }), { replace: true });
      } else {
        navigate('/onboarding?step=goals_selection');
      }
    } catch (error) {
      const message = getSignupErrorMessage(error);
      setFormError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    'AI-powered credit report analysis',
    'Smart dispute letter generation',
    'Funding readiness scoring',
    'Educational resources & courses'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex" data-testid="signup-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-8" data-testid="fundelite-signup-brand">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={brandTheme}
              logoClassName="h-10 w-auto max-w-[200px] object-contain object-left"
            />
          </div>
        </div>

        <div className="relative">
          <h2 className="text-4xl font-bold text-white leading-tight mb-6">
            Start Your Credit <br />Transformation
          </h2>
          <p className="text-lg text-white/80 max-w-md mb-8">
            Join thousands of users who have improved their credit scores and secured better funding opportunities.
          </p>
          
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 text-white/90">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check size={14} />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-white/60 text-sm">
          © 2026 FundElite AI. All rights reserved.
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8" data-testid="fundelite-signup-brand-mobile">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={brandTheme}
              logoClassName="h-9 w-auto max-w-[180px] object-contain"
            />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-stone-900">Create your account</h2>
            <p className="text-slate-500 mt-2">Start your credit transformation journey</p>
          </div>

          {formError ? (
            <div
              className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="signup-form-error"
              role="alert"
            >
              {formError}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-700">Full Name</Label>
              <IconInput
                id="name"
                name="name"
                type="text"
                icon={User}
                containerClassName="mt-1.5"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                data-testid="signup-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <IconInput
                id="email"
                name="email"
                type="email"
                icon={Mail}
                containerClassName="mt-1.5"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                data-testid="signup-email"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
              <IconInput
                id="phone"
                name="phone"
                type="tel"
                icon={Phone}
                containerClassName="mt-1.5"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={handleChange}
                data-testid="signup-phone"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <IconInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                data-testid="signup-password"
                trailing={(
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 z-[1] flex w-11 items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-[#EAF7F0]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
              <IconInput
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                icon={Lock}
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                data-testid="signup-confirm-password"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="signup-terms"
              />
              <span className="text-sm text-slate-600 leading-relaxed">
                I agree to the{' '}
                <a href="/terms" className="text-emerald-600 hover:text-emerald-700 underline" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-emerald-600 hover:text-emerald-700 underline" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </span>
            </label>

            <Button
              type="submit"
              className="w-full btn-primary py-3 mt-4"
              disabled={loading}
              data-testid="signup-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          {isGoogleLoginEnabled && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-slate-50 text-slate-500">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full py-3 border-slate-200 hover:bg-slate-50"
                onClick={handleGoogleSignup}
                data-testid="google-signup-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          <p className="text-center mt-4 text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
